import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../lib/api.js";
import type { AskEntry, ModelRef } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { useModelPicker } from "../ai/ModelPicker.js";

interface Props {
  projectId: string;
  onOpenScene: (sceneId: string) => void;
}

const SUGGESTIONS = [
  "What is happening in the story right now?",
  "What does Tyler currently want?",
  "What plot threads are still open?",
  "Where is Neon Saint first introduced?",
  "Every time Megan expresses concern about the streaming."
];

interface Pending {
  id: string;
  question: string;
  status: "pending" | "running" | "error";
  error?: string;
}

const uid = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

/**
 * Ask the Book — grounded manuscript interrogation. Answers factual questions
 * using only the manuscript, with clickable scene citations, and says when the
 * book doesn't establish something instead of guessing.
 *
 * Q&A is persisted server-side, so it survives reloads. Questions queue (ask
 * several without waiting; processed one at a time, newest on top). An answer
 * derived from older prose is flagged out of date and can be re-asked to refresh
 * it against the current manuscript. Model is chosen once per session.
 */
export function AskBookPanel({ projectId, onOpenScene }: Props) {
  const toast = useToast();
  const { pickModel } = useModelPicker();
  const [question, setQuestion] = useState("");
  const [entries, setEntries] = useState<AskEntry[]>([]);
  const [pending, setPending] = useState<Pending[]>([]);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const modelRef = useRef<ModelRef | null>(null);
  const inFlight = useRef<string | null>(null);

  // Load persisted history.
  useEffect(() => {
    let cancelled = false;
    api.listAsk(projectId)
      .then((r) => { if (!cancelled) setEntries(r.entries); })
      .catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, [projectId]);

  // Serial worker: run the oldest pending question; finishing one frees the next.
  useEffect(() => {
    if (inFlight.current) return;
    const next = pending.find((p) => p.status === "pending");
    if (!next) return;
    inFlight.current = next.id;
    setPending((prev) => prev.map((p) => (p.id === next.id ? { ...p, status: "running" } : p)));
    (async () => {
      try {
        const { entry } = await api.askBook(projectId, next.question, modelRef.current ?? undefined);
        setEntries((prev) => [entry, ...prev]);
        setPending((prev) => prev.filter((p) => p.id !== next.id));
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Ask failed";
        setPending((prev) => prev.map((p) => (p.id === next.id ? { ...p, status: "error", error: msg } : p)));
      } finally {
        inFlight.current = null;
      }
    })();
  }, [pending, projectId]);

  const enqueue = async (q: string) => {
    const query = q.trim();
    if (!query) return;
    if (!modelRef.current) {
      const choice = await pickModel({ task: "general-chat" });
      if (!choice) return;
      modelRef.current = choice;
    }
    setPending((prev) => [...prev, { id: uid(), question: query, status: "pending" }]);
    setQuestion("");
  };

  const setBusy = (id: string, on: boolean) =>
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });

  const reask = async (entry: AskEntry) => {
    if (!modelRef.current) {
      const choice = await pickModel({ task: "general-chat" });
      if (!choice) return;
      modelRef.current = choice;
    }
    setBusy(entry.id, true);
    try {
      const { entry: updated } = await api.reask(projectId, entry.id, modelRef.current ?? undefined);
      setEntries((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Re-ask failed");
    } finally {
      setBusy(entry.id, false);
    }
  };

  const remove = async (entry: AskEntry) => {
    setEntries((prev) => prev.filter((e) => e.id !== entry.id));
    try {
      await api.deleteAsk(projectId, entry.id);
    } catch {
      // Put it back if the delete failed.
      setEntries((prev) => [entry, ...prev]);
      toast.error("Could not delete that question.");
    }
  };

  const queued = pending.filter((p) => p.status !== "error").length;

  return (
    <div className="askbook">
      <section className="card insight-card">
        <div className="insight-card-head">
          <Icon name="search" size={16} style={{ color: "var(--accent)" }} />
          <h3>Ask the Book</h3>
          {queued > 0 && <span className="spacer" />}
          {queued > 0 && (
            <span className="faint" style={{ fontSize: 11 }}>
              <Icon name="clock" size={11} className="spin" /> {queued} in progress
            </span>
          )}
        </div>
        <p className="faint" style={{ fontSize: 12, margin: "0 0 10px" }}>
          Factual questions answered from your manuscript, with scene citations. It says when the book doesn't establish something rather than guessing. Ask several at once — they queue below and answer in order, and your Q&A is saved.
        </p>
        <div className="askbook-input">
          <input
            className="input"
            placeholder="Ask a question about your book…"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void enqueue(question); }}
          />
          <button className="btn btn-sm btn-primary" onClick={() => void enqueue(question)} disabled={!question.trim()}>
            <Icon name="search" size={13} /> Ask
          </button>
        </div>
        {entries.length === 0 && pending.length === 0 && (
          <div className="askbook-suggestions">
            {SUGGESTIONS.map((s) => (
              <button key={s} className="chip askbook-suggestion" onClick={() => void enqueue(s)}>{s}</button>
            ))}
          </div>
        )}
      </section>

      {/* In-flight / queued new questions (newest first). */}
      {[...pending].reverse().map((p) => (
        <section key={p.id} className={`card insight-card askbook-answer${p.status === "error" ? "" : " askbook-waiting"}`}>
          <div className="askbook-q">
            <Icon name="quote" size={13} className="faint" /> {p.question}
            {p.status === "error" && (
              <button className="btn btn-icon btn-ghost btn-sm" title="Dismiss" style={{ marginLeft: "auto" }}
                onClick={() => setPending((prev) => prev.filter((x) => x.id !== p.id))}>
                <Icon name="x" size={13} />
              </button>
            )}
          </div>
          {p.status === "error"
            ? <p className="intel-reason"><Icon name="alert" size={11} /> {p.error}</p>
            : <p className="askbook-a faint"><Icon name="clock" size={12} className={p.status === "running" ? "spin" : ""} /> {p.status === "running" ? "Searching the manuscript…" : "Queued…"}</p>}
        </section>
      ))}

      {/* Persisted answers. */}
      {entries.map((e) => {
        const busy = busyIds.has(e.id);
        return (
          <section key={e.id} className="card insight-card askbook-answer">
            <div className="askbook-q">
              <Icon name="quote" size={13} className="faint" /> {e.question}
              <span className="spacer" />
              <button className="btn btn-icon btn-ghost btn-sm" title="Re-ask (refresh against the current manuscript)" disabled={busy} onClick={() => void reask(e)}>
                <Icon name="redo" size={13} className={busy ? "spin" : ""} />
              </button>
              <button className="btn btn-icon btn-ghost btn-sm" title="Delete this question" disabled={busy} onClick={() => void remove(e)}>
                <Icon name="trash" size={13} />
              </button>
            </div>
            {e.stale && (
              <p className="intel-reason askbook-stale">
                <Icon name="alert" size={11} /> The manuscript changed since this was answered — re-ask to refresh.
              </p>
            )}
            <p className="askbook-a">{busy ? "Re-reading the manuscript…" : e.answer}</p>
            {!e.found && !busy && <p className="intel-reason"><Icon name="alert" size={11} /> Not established in the manuscript.</p>}
            {e.citations.length > 0 && !busy && (
              <div className="askbook-cites">
                {e.citations.map((c, j) => (
                  <button key={j} className="askbook-cite" onClick={() => onOpenScene(c.sceneId)} title={c.note}>
                    <Icon name="file" size={12} /> {c.label}
                  </button>
                ))}
              </div>
            )}
            <div className="faint" style={{ fontSize: 11, marginTop: 6 }}>
              Searched {e.scenesSearched} scene{e.scenesSearched === 1 ? "" : "s"}.
            </div>
          </section>
        );
      })}
    </div>
  );
}
